<template>
  <aside class="analytics-header" v-if="store.state.user_data && can_start" :key="key_ref">
    <div class="header">
      <div class="title">
        <h2>Analytics</h2>
        <queue-progress :isStatic="true" />
      </div>
      <!-- Creator Tag Filter -->
      <div class="creator-tag-dropdown" v-if="can_show_creator_tags" ref="tagDropdown">
        <el-button
          :type="show_tag_menu ? 'info' : 'default'"
          @click.stop="toggle_tag_dropdown"
          class="tag-button"
          :class="{ active: show_tag_menu }"
        >
          <SvgIcon name="tag" class="icon-left" />
          <span>{{ creator_tag_button_text }}</span>
          <SvgIcon name="arrow" class="icon-right" :class="{ rotate: show_tag_menu }" />
        </el-button>

        <!-- Dropdown Menu -->
        <div v-if="show_tag_menu" class="tag-dropdown-menu" @click.stop>
          <!-- Operation Type Selection -->
          <div class="operation-type-section">
            <label class="radio-item">
              <input type="radio" v-model="temp_operation_type" value="or" />
              <span>Or</span>
            </label>
            <label class="radio-item">
              <input type="radio" v-model="temp_operation_type" value="and" />
              <span>And</span>
            </label>
          </div>
          <!-- Divider Line -->
          <div class="divider-line"></div>
          <Scrollbar class="tag-list" :show_bar="true">
            <div
              v-for="tag in creator_tags"
              :key="tag.id"
              class="tag-item"
              :class="{ disabled: tag.id === 0 && temp_operation_type === 'and' }"
              @click="toggle_tag(tag)"
            >
              <input
                type="checkbox"
                :checked="tag.is_selected"
                :disabled="tag.id === 0 && temp_operation_type === 'and'"
                @click.stop
                @change="toggle_tag(tag)"
              />
              <span>{{ tag.text }}</span>
            </div>
          </Scrollbar>
          <!-- Footer buttons -->
          <div class="footer">
            <el-button type="default" @click="clear_tags" class="clear-btn">Clear</el-button>
            <el-button type="primary" @click="apply_tags" class="apply-btn">See results</el-button>
          </div>
        </div>
      </div>
      <!-- Date Picker moved to right side -->
      <div class="right-controls">
        <Dropdown
          v-if="!is_link_only && show_check_box"
          :items="data_source_items"
          :direction_type="'right'"
          :icon_direction_type="'right'"
          :default_value="data_source_default_index"
          @update-dropdown="onDataSourceChange"
        />
        <DatePicker
          class="btn"
          :date_range="date_range"
          @update-date="updateDate"
          @close="isClose"
          :is_post_pick="need_han_date ? true : false"
          :cus_text="cus_text"
        />
      </div>
    </div>
    <!-- Horizontal line below title -->
    <div class="header-divider"></div>
    <!-- Navigation row with tabs and update time -->
    <div class="nav-row">
      <Navi :menu="menu" />
      <div class="update-time" v-if="!can_show_checkbox || !show_check_box">
        Last Update: {{ store.state.user_data.merchant_data_update_hour_ago }}h ago
      </div>
    </div>
  </aside>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import SvgIcon from '@/components/ui/SvgIcon.vue';
import Navi from '@/components/Navi.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import moment from 'moment';
import { handleMaxMinFun } from '@/utils/SwitchFun.js';
import { getTags } from '@/utils/CreatorTagFun.js';
import Dropdown from '@/components/ui/Dropdown.vue';
import { ElButton } from 'element-plus';

const store = useStore();
const route = useRoute();
const { proxy } = getCurrentInstance();
const EventBus = proxy.EventBus;
const { $ProjectName } = proxy;

// Props
const props = defineProps({
  data_statistic: {
    type: Object,
    default: null,
  },
  show_check_box: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(['update_date', 'handleSortLink']);

// Template refs
const tagDropdown = ref(null);

// Data
const menu = ref([]);
const key_ref = ref(0);
const date_range = reactive({
  start_date: moment().add(-30, 'days').toDate(),
  end_date: moment().add(-1, 'days').toDate(),
});
const cus_text = ref('');
const can_start = ref(false);
const data_source = ref('all');
const data_source_items = [
  { text: 'All Data', value: 'all' },
  { text: 'Storefront Data', value: 'storefront' },
  { text: 'Shortlinks Data', value: 'shortlink' },
];
const creator_tags = ref([]);
const selected_tags = ref([]);
const temp_selected_tags = ref([]);
const tag_filter_key = ref(0);
const show_tag_menu = ref(false);
const is_loading_tags = ref(false);
const tag_operation_type = ref('or');
const temp_operation_type = ref('or');

// Computed
const data_source_default_index = computed(() => {
  return data_source_items.findIndex((item) => item.value === data_source.value);
});

const is_link_only = computed(() => {
  const merchant_is_link_only = store.state.user_data?.merchant_account.platform_type === 'linkonly';
  if (merchant_is_link_only && store.state.list_has_historical_storefront_tracking ) return false;
  return merchant_is_link_only;
});

const need_han_date = computed(() => {
  return store.state.user_data.contract_status && route.fullPath === '/analytics/content';
});

const creator_tag_button_text = computed(() => {
  if (selected_tags.value.length === 0) {
    return 'Creator Tag: All';
  }
  return `Creator Tag: Selected(${selected_tags.value.length})`;
});

const can_show_creator_tags = computed(() => {
  return can_start.value && !is_loading_tags.value && creator_tags.value.length > 1;
});

const plusData = computed(() => {
  let da = props.data_statistic;
  let can_toggle_sortlink = da ? da.short_link_click_sum + da.short_link_sales_sum + da.short_link_sales_value_sum : 0;
  return {
    can_toggle_sortlink: can_toggle_sortlink,
  };
});

const can_show_checkbox = computed(() => {
  return can_start.value && props.data_statistic && plusData.value.can_toggle_sortlink;
});

const is_advertiser = computed(() => {
  return true;
});
//++++++++++++++++++++++++++++++++++++++++++++++++++
// Watch
//++++++++++++++++++++++++++++++++++++++++++++++++++
watch(
  () => route.fullPath,
  (to) => {
    if (to === '/analytics/content') {
      date_range.start_date = need_han_date.value
        ? moment(store.state.user_data.contract_start_time).format('YYYY-MM-DD')
        : date_range.start_date;
      date_range.end_date = need_han_date.value ? moment().add(-1, 'days').toDate() : date_range.end_date;
    }
  },
  { immediate: true }
);


watch(temp_operation_type, (newVal) => {
  if (newVal === 'and') {
    const allTagsIndex = creator_tags.value.findIndex((t) => t.id === 0);
    if (allTagsIndex !== -1 && creator_tags.value[allTagsIndex].is_selected) {
      creator_tags.value[allTagsIndex].is_selected = false;
    }
  }
});

//++++++++++++++++++++++++++++++++++++++++++++++++++
// Methods
//++++++++++++++++++++++++++++++++++++++++++++++++++
const load_creator_tags = async () => {
  if (store.state.save_tags_loaded && store.state.save_analytics_tags.length > 0) {
    creator_tags.value = [...store.state.save_analytics_tags];
    selected_tags.value = store.state.save_selected_tags || [];
    temp_selected_tags.value = [...selected_tags.value];
    tag_filter_key.value++;
    return;
  }

  is_loading_tags.value = true;

  try {
    const tagsData = await getTags(proxy);
    if (tagsData && tagsData.data && tagsData.data.length > 0) {
      creator_tags.value = [
        { id: 0, text: 'All Tags', is_selected: true },
        ...tagsData.data.map((tag) => ({ ...tag, is_selected: false })),
      ];
      selected_tags.value = [];
      temp_selected_tags.value = [];
      store.state.save_analytics_tags = creator_tags.value;
      store.state.save_tags_loaded = true;
    } else {
      creator_tags.value = [];
      console.log('No creator tags available for this merchant');
    }
  } catch (error) {
    console.error('Failed to load creator tags:', error);
    creator_tags.value = [];
  } finally {
    is_loading_tags.value = false;
  }
};

const toggle_tag_dropdown = () => {
  show_tag_menu.value = !show_tag_menu.value;
  if (show_tag_menu.value) {
    temp_selected_tags.value = [...selected_tags.value];
    temp_operation_type.value = tag_operation_type.value;
    setTimeout(() => {
      document.addEventListener('click', handle_click_outside);
    }, 100);
  } else {
    document.removeEventListener('click', handle_click_outside);
  }
};

const handle_click_outside = (event) => {
  if (tagDropdown.value && !tagDropdown.value.contains(event.target)) {
    show_tag_menu.value = false;
    document.removeEventListener('click', handle_click_outside);
  }
};

const toggle_tag = (tag) => {
  if (tag.id === 0 && temp_operation_type.value === 'and') {
    return;
  }

  if (tag.id === 0) {
    creator_tags.value = creator_tags.value.map((t) => ({
      ...t,
      is_selected: t.id === 0,
    }));
    temp_selected_tags.value = [];
    temp_operation_type.value = 'or';
  } else {
    const tagIndex = creator_tags.value.findIndex((t) => t.id === tag.id);
    if (tagIndex !== -1) {
      creator_tags.value[tagIndex].is_selected = !creator_tags.value[tagIndex].is_selected;

      const tempIndex = temp_selected_tags.value.findIndex((t) => t.id === tag.id);
      if (tempIndex !== -1) {
        temp_selected_tags.value.splice(tempIndex, 1);
      } else {
        temp_selected_tags.value.push(tag);
      }

      if (temp_selected_tags.value.length > 0) {
        const allTagsIndex = creator_tags.value.findIndex((t) => t.id === 0);
        if (allTagsIndex !== -1) {
          creator_tags.value[allTagsIndex].is_selected = false;
        }
      } else {
        const allTagsIndex = creator_tags.value.findIndex((t) => t.id === 0);
        if (allTagsIndex !== -1) {
          creator_tags.value[allTagsIndex].is_selected = true;
          temp_operation_type.value = 'or';
        }
      }
    }
  }
};

const clear_tags = () => {
  creator_tags.value = creator_tags.value.map((tag) => ({
    ...tag,
    is_selected: tag.id === 0,
  }));
  temp_selected_tags.value = [];
  temp_operation_type.value = 'or';
};

const apply_tags = () => {
  selected_tags.value = [...temp_selected_tags.value];
  tag_operation_type.value = temp_operation_type.value;

  store.state.save_selected_tags = selected_tags.value;
  store.state.save_tag_operation_type = tag_operation_type.value;
  store.state.save_analytics_tags = [...creator_tags.value];

  EventBus.emit('analytics-tags-updated', {
    tags: selected_tags.value,
    operation_type: tag_operation_type.value,
  });

  show_tag_menu.value = false;
  document.removeEventListener('click', handle_click_outside);
};

const onDataSourceChange = (item) => {
  data_source.value = item.value;
  store.state.save_sortlink_data = item.value;
  emit('handleSortLink', item.value);
  EventBus.emit('update-sortlink', item.value);
};

const updateDate = (obj) => {
  date_range.start_date = obj.start_date;
  date_range.end_date = obj.end_date;
  cus_text.value = obj.cus;

  store.state.save_analytics_start_date = obj.start_date;
  store.state.save_analytics_end_date = obj.end_date;

  EventBus.emit('update-date-at-analytics', obj);
  EventBus.emit('update-date', obj);
};

const isClose = () => {
  setTimeout(() => {
    key_ref.value += 1;
  }, 100);
};

const startFunction = () => {
  if (store.state.save_analytics_start_date && store.state.save_analytics_end_date) {
    date_range.start_date = store.state.save_analytics_start_date;
    date_range.end_date = store.state.save_analytics_end_date;
  }

  let checkTime = handleMaxMinFun(date_range.start_date, date_range.end_date, proxy);
  date_range.start_date = checkTime[0];
  date_range.end_date = checkTime[1];
  const saved = store.state.save_sortlink_data;
  if (saved === 'all' || saved === 'storefront' || saved === 'shortlink') {
    data_source.value = saved;
  } else {
    data_source.value = saved ? 'all' : 'storefront';
  }
  can_start.value = true;
 
  const theLinkMenu = !is_link_only.value || (is_link_only.value && store.state.list_has_historical_storefront_tracking)
    ? [
        {
          text: 'Collections',
          to: '/analytics/collections',
        },
        {
          text: 'Posts',
          to: '/analytics/posts',
        },
      ]
    : [];


menu.value = [
    {
      text: 'Overview',
      to: '/analytics/overview',
    },
    {
      text: 'Products',
      to: '/analytics/products',
    },
    ...( store.state.show_advertisement ? [{ text: 'Advertisers', to: '/analytics/advertisers'}] : []),
    ...( store.state.user_data?.merchant_account?.is_enable_multi_brand ? [{ text: 'Brands', to: '/analytics/brands' }] : []),
    ...theLinkMenu,
    {
      text: 'Branded Content',
      to: '/analytics/content',
    },
  ];
};

// Lifecycle hooks
// Reset tags cache on page refresh
window.addEventListener('beforeunload', () => {
  store.state.save_tags_loaded = false;
});

const fetchListStatistic = () => {
  if (store.state.list_has_historical_storefront_tracking !== null) return Promise.resolve();
  const start = moment(date_range.start_date).format('YYYY-MM-DD');
  const end = moment(date_range.end_date).format('YYYY-MM-DD');
  return proxy.$ajax(`/creator/creator-list-statistic?start_date=${start}&end_date=${end}`, {
    method: 'get',
    withCredentials: true,
  }).then(res => {
    store.state.list_has_historical_storefront_tracking = !!res.data.data.has_historical_storefront_tracking;
    key_ref.value += 1; // Force re-render to update AnalyticsHeader based on new data
  });
};

onMounted(async () => {
  await fetchListStatistic();
  startFunction();

  if (store.state.save_selected_tags && store.state.save_selected_tags.length > 0) {
    selected_tags.value = store.state.save_selected_tags;
  }

  load_creator_tags();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handle_click_outside);
});
</script>

<style lang="scss">
.analytics-header {
  width: 100%;
  overflow: visible;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 21;
  padding-bottom: 16px;

  .header {
    width: 100%;
    overflow: visible;
    display: flex;
    align-items: center;
    padding-bottom: 16px;

    .title {
      display: flex;
      align-items: center;
      margin-right: 16px;

      h2 {
        font: var(--font-demi-36);
        color: black;
      }
    }

    .creator-tag-dropdown {
      position: relative;
      margin-right: auto;

      .tag-button {
        min-width: 172px;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 0 12px;
        border-radius: 6px !important;
        height: 32px !important;
        line-height: 32px !important;
        color: var(--black-60) !important;

        .icon-left {
          width: 16px;
          height: 16px;
          fill: var(--black-60);
        }

        span {
          flex: 1;
          text-align: left;
          gap: 4px;
        }

        .icon-right {
          width: 12px;
          height: 12px;
          fill: var(--black-60);
          transition: transform 0.2s;

          &.rotate {
            transform: rotate(180deg);
          }
        }
      }

      .tag-dropdown-menu {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        min-width: 240px;
        background: white;
        border: 1px solid var(--black-20);
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        z-index: 100;

        .operation-type-section {
          padding: 12px 20px;
          display: flex;
          gap: 24px;

          .radio-item {
            display: flex;
            align-items: center;
            cursor: pointer;

            input[type='radio'] {
              margin: 0 8px 0 0;
              width: 12px;
              height: 12px;
              accent-color: var(--dark-blue);
            }

            span {
              font: var(--font-fg-12);
              color: var(--black-90);
              user-select: none;
            }
          }
        }

        .divider-line {
          height: 1px;
          background-color: var(--black-20);
          margin: 0;
        }

        .tag-list {
          max-height: 300px;
          padding: 8px 0;

          .tag-item {
            display: flex;
            align-items: center;
            padding: 8px 16px;
            cursor: pointer;
            transition: background-color 0.2s;

            &:hover {
              background-color: var(--black-10);
            }

            input[type='checkbox'] {
              margin-right: 12px;
              cursor: pointer;
              width: 12px;
              height: 12px;
              -webkit-appearance: none;
              appearance: none;
              border: 1px solid var(--black-50);
              border-radius: 50%; // Make circular
              background-color: white;
              vertical-align: middle;

              &:checked {
                background-color: var(--dark-blue);
                border-color: var(--dark-blue);
                position: relative;

                // Add checkmark - adjusted for 12px size
                &::after {
                  content: '\2713';
                  position: absolute;
                  color: white;
                  font-size: 10px;
                  font-weight: bold;
                  top: -1px;
                  left: 1px;
                }
              }

              &:hover:not(:disabled) {
                border-color: var(--black-75);
              }

              &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
              }
            }

            span {
              font: var(--font-fg-12);
              color: var(--black-90);
              user-select: none;
            }

            &.disabled {
              opacity: 0.5;
              cursor: not-allowed;
              pointer-events: none;

              &:hover {
                background-color: transparent;
              }
            }
          }
        }

        .footer {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          background-color: transparent;
          justify-content: flex-end;

          .clear-btn {
            min-width: 60px;
            height: 28px;
            line-height: 26px;
            border-radius: 6px;
            font-size: 12px;
            padding: 0 16px;
            border: 1px solid var(--black-40);
            background: transparent;
            color: var(--black-80);

            &:hover {
              background-color: var(--black-10);
            }
          }

          .apply-btn {
            min-width: 80px;
            height: 28px;
            line-height: 26px;
            border-radius: 6px;
            font-size: 12px;
            padding: 0 16px;

            &:hover {
              background-color: var(--dark-blue-hover, #2c5aa0);
            }
          }
        }
      }
    }

    .right-controls {
      display: flex;
      align-items: center;
      margin-left: auto;
    }

    .update-time {
      color: var(--black-60);
      font: var(--font-fg-12);
      margin-right: 20px;
    }

    .btn {
      margin-left: 8px;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        margin-right: 6px;
      }
    }
  }

  .header-divider {
    width: 100%;
    height: 2px;
    background-color: var(--black-20);
    margin-bottom: 16px;
  }

  .nav-row {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .ui-navi {
      flex: 1;
    }

    .update-time {
      color: var(--black-60);
      font: var(--font-fg-12);
      margin-left: 20px;
      flex-shrink: 0;
    }
  }

  .ui-navi {
    width: 100%;
  }
}
</style>
